import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { Tabs } from '@mui/material';
import React, { ReactNode } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { filterRoles, hasRole, hasRouteAccess, useIsAuthenthicated } from '../auth';
import LinkTab, { ClientLink, ServerLink } from '../link-tab/LinkTab';
import { UserRole } from '../types';
import { Pages } from '../types/pages';
import { useActiveCompany } from '../utils/useCompany';
import PositionedMenu from './PositionedMenu';

export interface MenuProps {
  onItemClick: () => void;
}

export type MenuTabProps = {
  hidden?: boolean;
  label: string;
  roles?: string[];
  component?: ReactNode;
} & (ServerLink | ClientLink);

const tabsStyle = { display: 'flex', gap: 2, textTransform: 'uppercase' };

const Menu = ({ onItemClick }: MenuProps) => {
  const isAuthenticated = useIsAuthenthicated();
  const { pathname } = useLocation();
  const company = useActiveCompany();
  const routeHidden = !hasRouteAccess();

  const authTabs: MenuTabProps[] = [
    { label: 'Teach', to: Pages.Teach, roles: [UserRole.Instructor] },
    { label: 'Learn', to: Pages.Learn, roles: [UserRole.Student] },
    { label: 'Home', to: '/home', roles: [UserRole.Member] },
    {
      label: 'Explore',
      href: '/v2/i/marketplace',
      roles: [UserRole.Instructor, UserRole.Member],
      component: <PositionedMenu key="Explore" />,
    },
    { label: 'Simulators', href: '/v2/i/simulators', roles: [UserRole.Instructor] },
    { label: 'Exercises', href: '/v2/i/exercise', roles: [UserRole.Instructor] },
    {
      label: 'Routes',
      key: 'Routes Instructor',
      hidden: routeHidden,
      href: '/v2/i/routes',
      roles: [UserRole.Instructor],
    },
    { label: 'Routes', key: 'Routes Student', hidden: routeHidden, href: '/v2/s/routes', roles: [UserRole.Student] },
    { label: 'Users', href: '/v2/i/user-and-groups', roles: [UserRole.Instructor] },
    { label: 'Academy', href: '/v2/i/academy', roles: [UserRole.AreaDeveloper] },
  ].filter(tab => !tab.hidden && company && hasRole(tab.roles, filterRoles(company?.roles)));

  const unauthTabs: MenuTabProps[] = [{ label: 'Login', to: '/login' }];
  const selectedTabs = (isAuthenticated ? authTabs : unauthTabs).map(
    tab => matchPath({ path: (tab.to ?? tab.href)!, end: false }, pathname)?.pathname,
  );
  const selectedTab = selectedTabs.find(Boolean) ?? false;

  const MenuTab = (props: MenuTabProps & { value: string }) => <LinkTab {...props} onClick={onItemClick} />;

  return (
    <>
      <AuthenticatedTemplate>
        <Tabs sx={tabsStyle} scrollButtons="auto" variant="scrollable" value={selectedTab}>
          {authTabs.map(({ roles, ...tab }) =>
            tab?.component ? tab?.component : <MenuTab key={tab.label} {...tab} value={(tab.to ?? tab.href)!} />,
          )}
        </Tabs>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <Tabs sx={tabsStyle} scrollButtons="auto" variant="scrollable" value={selectedTab}>
          {unauthTabs.map(tab => (
            <MenuTab key={tab.label} {...tab} value={(tab.to ?? tab.href)!} />
          ))}
        </Tabs>
      </UnauthenticatedTemplate>
    </>
  );
};

export default Menu;
